import { PaymentMethodPayload } from "braintree-web-drop-in";
import { ParsedQuery } from "query-string";
import { Message } from "./components/ui/Notification/Notification";
import { CurrencySymbolsConfig, PaymentMethodsConfig } from "./state";

/**
 * Request types
 */
export type ConfigData = string | string[];
export type TranslationData = string | string[];
export interface UserRegistrationData {
  emailAddress: string,
  over18: boolean,
  termsAccepted: boolean,
  marketingAccepted: boolean,
  password: string,
  source: string,
  params?: DataParams
}
export type UserSigninData = {
  userName: string,
  password: string,
  params?: DataParams
}
export type VoucherData = { voucherCode: string };
export type VodafonePayData = {
  data: { id: string, productId: number, requestHeaders: any }
}

/**
 * Response types
 */
export interface BillingStatusRes {
  billingStatus: string,
  paymentCardNumber: string,
  paymentCardBrand: string,
  purchases: {
    cancelled: boolean
    id: string
    nextBillDate: string
    paymentProvider: string
    paymentSourceId: string
    previousBillDate: string
    product: any
    startBillDate: string
    userId: string
    endBillDate: string
  }[],
  totalSubscriptionDurationHours: number,
  userId: string,
  vouchers: any
}

export interface PurchaseRes {
  cancelled: boolean,
  id: string
  nextBillDate: string
  paymentProvider: string
  paymentSourceId: string
  previousBillDate: string
  product: any
  startBillDate: string
  userId: string
}
export interface UserRes {
  actorStatus: string;
  apiKey: string;
  apiSecret: string;
  avatar: string;
  campaign: string;
  cdnExpiry: number;
  cdnSessionId: string;
  countryCode: string;
  displayName: string;
  durationInDays: number;
  durationInMonths: number;
  emailAddress: string;
  error: boolean;
  id: string;
  inviteCode: string;
  marketingAccepted: boolean;
  organization: string;
  over18: boolean;
  payment: PaymentMethodPayload | any;
  partner: string;
  percentageDiscount: number;
  pinNumber: string;
  productId: number;
  profileName: string;
  termsAccepted: boolean;
  trackingAccepted: boolean;
  subscription: {
    cancelled: boolean;
    endBillDate: any;
    nextBillDate: string;
    payment: any;
    paymentProvider: string;
    paymentSourceId: string;
    previousBillDate: any;
    productId: number;
    startBillDate: string;
    userId: string;
  };
  voucherCode: string;
  voucherID: string;
}

export interface ErrorRes
{
  timestamp: string,
  status: number,
  error: boolean,
  message: string,
  trace: string,
  qid: string,
  path: string
}

export type PlansResponseData = Plan[];
export type VoucherResponseData = Plan[];
export interface TvCodeRes {
  tvToken: string;
}

export type SkipPayment = {
  skip: boolean,
  planId: number
}

export type PaymentButtons = {
  showCancelButton: boolean,
  showRenewButton: boolean,
  showConfirmButtons: boolean;
}

/**
 * State actions
 */
export type AppActions =
  | { type: 'ADD_MESSAGE', payload: Message }
  | { type: 'DELETE_MESSAGE', payload: number }
  | { type: 'ERROR_VODAFONE', payload: any }
  | { type: 'SET_PLANS', payload: any }
  | { type: 'UPDATE_BILLING_STATUS', payload: any }
  | { type: 'UPDATE_CONFIG', payload: any }
  | { type: 'UPDATE_FORM_DATA', payload: any }
  | { type: 'UPDATE_QUERY_PARAMS', payload: ParsedQuery<string> }
  | { type: 'UPDATE_PLANS', payload: Plan }
  | { type: 'UPDATE_FALLBACK_TRANSLATIONS', payload: any }
  | { type: 'UPDATE_LOCALE_TRANSLATIONS', payload: any }
  | { type: 'UPDATE_DEFAULT_THEME', payload: ThemeProps }
  | { type: 'UPDATE_USER', payload: any }
  | { type: 'UPDATE_SKIP_PAYMENT', payload: SkipPayment }
  | { type: 'UPDATE_PAYMENT_BUTTONS', payload: PaymentButtons }

/**
 * App state
 */

export interface AppState {
  billingStatus: BillingStatusRes,
  config: {
    theme: ThemeProps;
    paymentMethods: PaymentMethodsConfig;
    currencySymbols: CurrencySymbolsConfig;
    blacklistedPlans: String[];
    version: string;
    shouldShowPageCounter: boolean;
    partnerID: string;
    partnerUsesPaylessFlow: boolean;
  };
  form: {
    [key: string]: any;
    emailAddress: string;
    marketingAccepted: boolean;
    over18: boolean;
    selectedPlan: Plan | null;
    termsAccepted: boolean;
    userName: string,
    voucherCode: string;
  };
  messages: Message[];
  //TODO: we keep having partners choose different variable names for effectively the same piece of data (accessCode, userID, token)
  //ask if we can normalise this for web billing recreation
  params: ParamsConfig;
  plans: Plan[];
  translations: any;
  user: UserRes
  stateVersion: string;
  skipPayment: {
    skip: boolean,
    planId: number
  };
  paymentButtons: PaymentButtons;
}

export interface Copy {
  fallbackCopy?: any;
  localeCopy: any;
}
export enum BillingCycles {
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  BIANNUAL = '6 month',
  ANNUAL = 'Yearly',
  BIENNIAL = 'Biennial'
}

//List of URL Paramaters the app can accept.
export interface ParamsConfig {
  config: string; 
  locale: string; 
  voucher: string; 
  accessCode?: string, 
  userID?: string, 
  token?: string
}

//Type used to define parameter data that can be sent in login/registration APIs.
export type DataParams = {
  "PARTNER": string,
  [key: string]: string
}

export interface Plan {
  billingCycle: BillingCycles
  currency: string
  discountPrice: number
  discounts: Discounts[]
  productId: number
  totalPrice: number
  currencySymbols: CurrencySymbolsConfig
}

export interface Discounts {
  percentage: number
  durationInMonths: number,
  partner: string
  onlyValidWithPayment: boolean
}

export interface UserVoucherData {
  voucherCode: string,
  campaign: string,
  organization: string,
  percentageDiscount: number,
  durationInMonths: number,
  durationInDays: number,
  onlyValidWithPayment: boolean,
  id: string,
  productId: number
}

export type DataCookieDefinitionsType = {
  id: string;
  apiKey: string;
  apiSecret: string;
  kalturaToken: string;
}

export enum ROUTES {
  DASHBOARD = '/',
  PAYMENT = '/subscribe/payment',
  PAYMENT_FAILURE = '/payment-failure',
  PAYMENT_SUCCESS = '/payment-success',
  PLAN_SELECTOR = '/subscribe/plan-selector',
  USER_FORGOTTEN_PASSWORD = '/user/forgotten-password',
  USER_LOGIN = '/user/login',
  USER_REGISTRATION = '/user/registration',
}

export type ThemeProps = {
  mq: {
    desktop: string
    smallMobile: string
  },
  colors: {
    accent: string,
    background: {
      backdrop: string,
      paper: string,
    },
    common: {
      black: string,
      darkGrey: string,
      lightGrey: string,
      grey: string,
      transparent: string,
      white: string
    },
    error: string,
    text: {
      accent: string,
      error: string,
      muted: string,
      primary: string,
      secondary: string,
      success: string
      white: string,
    },
    success: string,
    warning: string
  },
  fonts: {
    families: string,
    weights: { light: number, medium: number, bold: number },
    urls: string[]
  },
  images: {
    background: string,
    hero: string,
    icon: string,
    device: string
  },
  radii: {
    accordion: number,
    basket: number,
    button: number,
    card: number,
    inputs: number
  },
  shadows: {
    accordion: string,
    card: string,
    button: string,
    inputs: string
  }
  version: string
}

export enum TrackingPage {
  PLAN_SELECTOR = 'SelectPlan',
  LOGIN = 'Login',
  USER_REGISTRATION = 'CreateAccount',
  PAYMENT = 'SelectPaymentType',
  PAYMENT_SUCCESS = 'Success'
}

export enum TrackingAction {
  PAGE_LOAD = 'PageLoad',
  SELECT_PLAN = 'SelectPlan',
  VOUCHER_CODE_ENTERED = 'VoucherTextEntered',
  VOUCHER_APPLY = 'VoucherApplyButton',
  VOUCHER_APPLY_SUCCESS = 'VoucherDiscountAdded',
  VOUCHER_APPLY_FAILURE = 'VoucherFailure',
  CONTINUE_BUTTON_CLICK = 'ContinueButton',
  LOGIN_BUTTON_CLICK = 'LoginButton',
  EMAIL_ENTERED = 'EmailEntered',
  EMAIL_REJECTED = 'EmailRejected',
  PASSWORD_ENTERED = 'PasswordEntered',
  PASSWORD_REJECTED = 'PasswordRejected',
  LOGIN_FAILURE = 'LoginFailure',
  GDPR_AGE_OPT_IN = 'GdprAgeTicked',
  GDPR_TANDC_OPT_IN = 'GdprTandCTicked',
  GDPR_MARKETING_OPT_IN = 'GdprMarketingTicked',
  PAYMENT_METHOD_SELECTED = 'MethodSelected',
  SUBMIT_PAYMENT_BUTTON_CLICK = 'SubmitPaymentButton',
  PAYMENT_FAILURE = 'PaymentFailure',
  PAYMENT_SUCCESS = 'PaymentSuccessful',
}


export enum TrackingProperty {
  TEXT_VARIANT = 'TextVariant',
  LANGUAGE = 'Language',
  CURRENCY = 'Currency',
  PLAN_DURATION_MONTHS = 'PlanDuration',
  PLAN_PRICE = 'PlanValue',
  DISCOUNT_PERCENT = 'PercentDiscount',
  DISCOUNT_DURATION_MONTHS = 'PercentDuration',
  VOUCHER_CODE = 'VoucherCode',
  VOUCHER_CAMPAIGN = 'VoucherCampaign',
  VOUCHER_ORGANISATION = 'VoucherOrganisation',
  ERROR_TYPE = 'ErrorType',
  PAYMENT_METHOD = 'PaymentMethod',
}

export type TrackingPropertyMap = {
  [key in TrackingProperty]?: string | number;
};

/* 
This solution would mean code changes required for each new theme.
Leaving this here for now but with the new String implementation of products/config we should remove soon.
*/
export type Product = 'azoomee' | 'dvmkids' | 'azoomee-vodafone'

//Declare dataLayer for Google Tag Manager
declare global {
  interface Window {
      dataLayer: any;
  }
}